/**
 * TrustDetailScreen — read-only vendor trust score detail.
 * canonical: S3 — docs/v3_canonical.html lines 1940–2013
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchTrustScore } from '../../components/trust/trustApi'
import { fetchTrustCoaching, fetchTrustScoreEvents } from '../../api/subs'
import TrustScoreChip from '../../components/trust/TrustScoreChip'
import SubVendorActionsMenu from '../../components/subs/SubVendorActionsMenu'
import { useSubVendorInfo } from './useSubVendorInfo'
import { MAX_TRUST_SCORE } from '../../components/trust/types'

function formatEventLabel(eventType: string): string {
  return eventType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

export default function TrustDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const vendorUserId = parseInt(id ?? '', 10)

  const validId = Number.isFinite(vendorUserId) && vendorUserId > 0

  const { data: vendorInfo, isPending: vendorInfoLoading } = useSubVendorInfo(vendorUserId, validId)

  const { data: score, isPending: scoreLoading, isError: scoreError } = useQuery({
    queryKey: ['trust-score', vendorUserId],
    queryFn:  () => fetchTrustScore(vendorUserId),
    enabled:  validId,
  })

  const { data: events = [], isPending: eventsLoading } = useQuery({
    queryKey: ['trust-score-events', vendorUserId],
    queryFn:  () => fetchTrustScoreEvents(vendorUserId),
    enabled:  validId,
  })

  const { data: coaching } = useQuery({
    queryKey: ['trust-coaching', vendorUserId],
    queryFn:  () => fetchTrustCoaching(vendorUserId),
    enabled:  validId,
  })

  const companyName = vendorInfo?.companyName
    ?? (vendorInfoLoading ? 'Loading…' : `Vendor #${vendorUserId}`)

  if (!validId) {
    return (
      <div className="pc">
        <div style={{ padding: 24, color: 'var(--red)', fontSize: 13 }}>Invalid vendor.</div>
      </div>
    )
  }

  return (
    <>
      <div className="topbar">
        <div className="crumb">
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/subs')}>Subs &amp; Vendors</span>
          {' · '}
          <b>Trust detail</b>
        </div>
        <div className="topbar-r">
          <button type="button" className="btn b-gh btn-sm" onClick={() => navigate('/subs')}>
            ← Back to list
          </button>
        </div>
      </div>

      <div className="pc">
        <div
          data-testid="trust-page-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 14,
          }}
        >
          <h1
            data-testid="trust-vendor-heading"
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--ink)',
              letterSpacing: '-.01em',
            }}
          >
            {companyName}
          </h1>
          {vendorInfo && (
            <SubVendorActionsMenu
              vendorId={vendorInfo.vendorId}
              vendorUserId={vendorUserId}
              companyName={companyName}
              ownerEmail={vendorInfo.email}
              testId="trust-actions-menu"
            />
          )}
        </div>

        {scoreLoading && (
          <div style={{ fontSize: 13, color: 'var(--ink4)' }}>Loading trust score…</div>
        )}

        {scoreError && (
          <div style={{ fontSize: 13, color: 'var(--red)' }}>Could not load trust score.</div>
        )}

        {score && (
          <>
            <div className="trust-hero">
              <div>
                <div className="trust-num">{score.effectiveScore}</div>
                <div className="trust-of">of {MAX_TRUST_SCORE}</div>
              </div>
              <div className="trust-summary">
                <div style={{ marginTop: 0 }}>
                  <TrustScoreChip score={score} />
                </div>
                {score.isOverridden && (
                  <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 6 }}>
                    Manual override active
                  </div>
                )}
              </div>
            </div>

            {coaching?.coaching && (
              <div className="panel" style={{ marginBottom: 14 }}>
                <div className="panel-h">
                  <div className="panel-tt">Coaching notes</div>
                  <span className="panel-st">{coaching.tierLabel}</span>
                </div>
                <div style={{ padding: '14px 16px', fontSize: 13, lineHeight: 1.55 }}>
                  <div style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>
                    {coaching.coaching.headline}
                  </div>
                  <div style={{ color: 'var(--ink3)' }}>{coaching.coaching.detail}</div>
                </div>
              </div>
            )}

            <div className="panel">
              <div className="panel-h">
                <div className="panel-tt">Score events</div>
                <span className="panel-st">Recent history</span>
              </div>
              {eventsLoading && (
                <div style={{ padding: 16, fontSize: 12, color: 'var(--ink4)' }}>Loading events…</div>
              )}
              {!eventsLoading && events.length === 0 && (
                <div style={{ padding: 16, fontSize: 12, color: 'var(--ink4)' }}>No events recorded yet.</div>
              )}
              {!eventsLoading && events.length > 0 && (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Event</th>
                      <th className="r">Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(ev => (
                      <tr key={ev.id}>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {new Date(ev.created_at).toLocaleDateString()}
                        </td>
                        <td>{formatEventLabel(ev.event_type)}</td>
                        <td
                          className="r mono-c"
                          style={{
                            color: ev.delta > 0 ? 'var(--green)' : ev.delta < 0 ? 'var(--red)' : 'var(--ink3)',
                          }}
                        >
                          {ev.delta > 0 ? `+${ev.delta}` : ev.delta}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}
